import { useState, useEffect, useCallback } from 'react';
import { X } from 'react-feather';
import { FIREBASE_AUTH } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';

// PPL-36 soft gate — persistent (per-session dismissible) banner nudging
// email/password users who never clicked their verification link. Advisory
// only: nothing is blocked. Re-checks on tab focus so verifying in another
// tab hides the banner without a full reload.
const VerifyEmailBanner = () => {
  const { user, sendVerification } = useAuth();
  const [needsVerify, setNeedsVerify] = useState(false);
  const [sendState, setSendState] = useState('idle'); // idle | sending | sent | error
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('verifyEmailBannerDismissed') === '1'
  );

  const evaluate = useCallback(async (reload) => {
    let current = FIREBASE_AUTH.currentUser;
    if (!current) {
      setNeedsVerify(false);
      return;
    }
    if (reload) {
      try {
        await current.reload();
      } catch {
        // Non-fatal — keep the last known state.
      }
      current = FIREBASE_AUTH.currentUser;
    }
    const isPasswordUser = current?.providerData?.some(p => p.providerId === 'password');
    setNeedsVerify(!!current && isPasswordUser && !current.emailVerified);
  }, []);

  useEffect(() => {
    // reload:true — the persisted Firebase user keeps a stale emailVerified,
    // so a user who verified on another device would otherwise see the banner
    // until a tab-visibility change forced the refresh.
    evaluate(true);
  }, [user?.uid, evaluate]);

  useEffect(() => {
    if (!needsVerify) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') evaluate(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [needsVerify, evaluate]);

  if (!needsVerify || dismissed) return null;

  const handleResend = async () => {
    setSendState('sending');
    try {
      await sendVerification();
      setSendState('sent');
    } catch {
      setSendState('error');
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('verifyEmailBannerDismissed', '1');
    setDismissed(true);
  };

  return (
    <div className="verify-email-banner" role="status">
      <span>
        please verify your email — we sent a link to <strong>{user?.email}</strong>
      </span>
      <div className="verify-email-banner__actions">
        {sendState === 'sent' ? (
          <span className="verify-email-banner__sent">link sent ✓</span>
        ) : (
          <button
            className="verify-email-banner__resend"
            onClick={handleResend}
            disabled={sendState === 'sending'}
          >
            {sendState === 'sending' ? 'sending…' : sendState === 'error' ? 'try again' : 'resend link'}
          </button>
        )}
        <button className="verify-email-banner__close" onClick={handleDismiss} aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
